import { api } from "../api";

export interface PatronType {
  id: string;
  name: string;
}

export interface Location {
  id: string;
  name: string;
  code?: string | null;
}

export interface Collection {
  id: string;
  name: string;
  code?: string | null;
}

export interface ItemType {
  id: string;
  tenantId: string;
  name: string;
  code: string;
  description?: string | null;
  isBorrowable: boolean;
  loanPeriodOverride?: number | null;
  fineAmountOverride?: number | null;
  maxRenewalsOverride?: number | null;
  defaultLocationId?: string | null;
  defaultCollectionId?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  defaultLocation?: Location | null;
  defaultCollection?: Collection | null;
  allowedPatronTypes?: Array<{
    patronType: PatronType;
  }> | null;
}

export interface CreateItemTypeDto {
  name: string;
  code: string;
  description?: string;
  isBorrowable?: boolean;
  loanPeriodOverride?: number;
  fineAmountOverride?: number;
  maxRenewalsOverride?: number;
  defaultLocationId?: string;
  defaultCollectionId?: string;
  allowedPatronTypeIds?: string[];
}

export type UpdateItemTypeDto = Partial<CreateItemTypeDto>;

export const itemTypesApi = {
  /**
   * Get all item types for the current tenant (authenticated)
   */
  getAll: async (includeInactive = false): Promise<ItemType[]> => {
    const response = await api.get("/item-types", {
      params: { includeInactive },
    });
    return response.data;
  },

  /**
   * Get a single item type by ID
   */
  getById: async (id: string): Promise<ItemType> => {
    const response = await api.get(`/item-types/${id}`);
    return response.data;
  },

  /**
   * Create a new item type
   */
  create: async (data: CreateItemTypeDto): Promise<ItemType> => {
    const response = await api.post("/item-types", data);
    return response.data;
  },

  /**
   * Update an existing item type
   */
  update: async (id: string, data: UpdateItemTypeDto): Promise<ItemType> => {
    const response = await api.put(`/item-types/${id}`, data);
    return response.data;
  },

  /**
   * Update patron type restrictions for an item type
   */
  setPatronRestrictions: async (id: string, patronTypeIds: string[]): Promise<ItemType> => {
    const response = await api.put(`/item-types/${id}/restrictions`, { patronTypeIds });
    return response.data;
  },

  /**
   * Delete an item type
   * @param id - Item type ID
   * @param hardDelete - If true, permanently delete. If false or undefined, soft delete (set inactive).
   */
  delete: async (id: string, hardDelete?: boolean): Promise<void> => {
    const params = hardDelete ? { hardDelete: "true" } : {};
    await api.delete(`/item-types/${id}`, { params });
  },
};
